// Τα αρχικά ενός ονόματος, για τον αντικαταστάτη της εικόνας προφίλ.
//
// **Καθαρή συνάρτηση και όχι λογική μέσα στο συστατικό**, γιατί ο κανόνας ισχύει σε
// **τρεις** θέσεις — την κεφαλίδα, το σήμα συντάκτη και τη σελίδα προφίλ — και
// γραμμένος τρεις φορές είναι κανόνας που θα αποκλίνει. Είχε ήδη αποκλίνει μία
// φορά, όταν η κεφαλίδα κρατούσε δικό της υπολογισμό δίπλα σε εκείνον του σήματος.
//
// **Ο κανόνας:** ένα αρχικό ανά λέξη, **το πολύ τρία**. Η **παύλα μετρά ως
// διαχωριστικό λέξεων**, γιατί το «Άννα-Μαρία» είναι δύο ονόματα και ο αναγνώστης
// τα διαβάζει ως δύο: χωρίς αυτό, η ίδια γυναίκα θα είχε άλλα αρχικά ανάλογα με το
// αν κάποιος πληκτρολόγησε κενό ή παύλα.
//
// **Τα κεφαλαία δεν φέρουν τόνο στα ελληνικά**, οπότε το «Άννα» δίνει «Α» και όχι
// «Ά». Τα **διαλυτικά όμως μένουν**: δεν είναι τόνος αλλά ένδειξη χωριστής
// προφοράς, και το κεφαλαίο τους είναι «Ϊ».
//
// **Και τα δύο τα κάνει η μετατροπή σε κεφαλαία με ελληνική τοπικότητα**, μία
// κλήση. Η πρώτη γραφή είχε από πάνω και αποσύνθεση NFD με αφαίρεση συνδυαστικών
// σημείων — αντιγραμμένη από την κανονική μορφή του κειμένου αναζήτησης, όπου
// είναι σωστή επειδή εκεί το ζητούμενο είναι να **ταιριάζουν** «Ϊ» και «Ι».
// **Μετρήθηκε, και ήταν λάθος εδώ:** έδινε «Ι» αντί για «Ϊ», δηλαδή χαλούσε την
// ορθογραφία για να λύσει πρόβλημα που η τοπικότητα είχε ήδη λύσει. Η μετάλλαξη
// που την αφαίρεσε **δεν έριξε καμία δοκιμή** — και αυτό ήταν το στοιχείο ότι δεν
// έκανε τη δουλειά που της αποδιδόταν.

/** Διαχωριστικά λέξεων: κενά και **κάθε είδους παύλα**. */
const WORD_BREAK = /[\s-‐‑‒–—]+/;

const MAX_INITIALS = 3;

/**
 * Το αρχικό μιας λέξης, κεφαλαίο και **χωρίς τόνο**.
 *
 * Η ελληνική τοπικότητα δεν είναι διακοσμητική: `'ά'.toUpperCase()` δίνει «Ά», ενώ
 * με τοπικότητα δίνει «Α». Η εξάρτηση από τους κανόνες του περιβάλλοντος είναι
 * **δηλωμένη και φυλαγμένη** — οι δοκιμές ελέγχουν το αποτέλεσμα, οπότε περιβάλλον
 * χωρίς ελληνικούς κανόνες θα τις ρίξει αντί να αλλάξει σιωπηλά την οθόνη.
 */
function initialOf(word) {
    return word.charAt(0).toLocaleUpperCase('el-GR');
}

/**
 * @param user Ό,τι σχήμα χρήστη έχει η οθόνη. Η σειρά προτίμησης είναι η ίδια
 *   παντού: ονοματεπώνυμο, αλλιώς όνομα χρήστη.
 * @returns Ένα ως τρία γράμματα, ή `?` όταν δεν υπάρχει τίποτα να δείξει.
 */
export function initialsOf(user) {
    // **Το `trim` πριν από την επιλογή και όχι μετά.** Ο εξυπηρετητής επιστρέφει τα
    // προαιρετικά πεδία ως **κενό αλφαριθμητικό** και όχι ως απουσία — γι' αυτό `||`
    // και όχι `??` — αλλά η ίδια περίπτωση έχει και δεύτερη μορφή: ονοματεπώνυμο με
    // **μόνο κενά**, που είναι αληθές ως τιμή και άδειο ως όνομα. Με το `trim` στο
    // τέλος, η επιλογή έπεφτε πάνω του και ο κύκλος έμενε με ερωτηματικό ενώ το
    // όνομα χρήστη υπήρχε δίπλα. Το βρήκε η δοκιμή, όχι η ανάγνωση.
    const source = [user?.fullName, user?.username]
        .map((value) => String(value ?? '').trim())
        .find(Boolean);

    if (!source) return '?';

    const letters = source
        .split(WORD_BREAK)
        .filter(Boolean)
        .map(initialOf)
        // Λέξη που είναι μόνο σημείο στίξης δίνει κενό αρχικό και δεν μετρά ως λέξη.
        .filter(Boolean)
        .slice(0, MAX_INITIALS)
        .join('');

    return letters || '?';
}

export default initialsOf;
